import { toApiSortOrder, UnitPayoutStatus } from "@/features/financialShared";
import type {
	GetRevenueServicePeriodUnitsArgs,
	RevenuePeriodFileBody,
	RevenueServicePeriodUnitsResponse,
	UpdateRevenueUnitStatementBody,
} from "./revenueManagement.api.types";
import {
	mapRevenuePayoutMethodLabel,
	mapRevenuePayoutMethodToApi,
	mapRevenueStatusFromApi,
	mapRevenueUnitStatusFromApi,
} from "./revenueManagement.api.types";
import type {
	RevenueActivityDetailRow,
	RevenueActivitySummaryRow,
	RevenueOwnerInvoiceDetail,
	RevenuePeriodUnitsHeader,
	RevenueUnitDetail,
	RevenueUnitListItem,
	RevenueUnitOwner,
	RevenueUnitPayout,
} from "./revenueManagement.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function toDateInputValue(raw: string): string {
	const trimmed = raw.trim();
	if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function parseNamedPerson(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name ?? row.fullName).trim();
}

function initialsFromName(name: string): string {
	return name
		.replace(/\b(mr|ms|mrs|dr)\b\.?/gi, "")
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

export function spreadsheetMimeType(file: File): string {
	if (file.type) return file.type;
	if (file.name.toLowerCase().endsWith(".xls")) {
		return "application/vnd.ms-excel";
	}
	return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
}

export function toRevenuePeriodFileBody(
	file: File,
	filePath: string,
): RevenuePeriodFileBody {
	return {
		filePath,
		fileName: file.name,
		mimeType: spreadsheetMimeType(file),
		fileSize: file.size,
	};
}

export function parseRevenuePeriodUnitsHeader(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): RevenuePeriodUnitsHeader {
	const row = asRecord(raw) ?? {};
	return {
		propertyId: asString(row.propertyId, fallback.propertyId),
		propertyName: asString(row.propertyName).trim() || "Untitled property",
		periodId: asString(row.periodId, fallback.periodId),
		periodStart: asString(row.periodStart),
		periodEnd: asString(row.periodEnd),
		periodLabel:
			asString(row.periodLabel).trim() ||
			[asString(row.periodStart), asString(row.periodEnd)]
				.filter(Boolean)
				.join(" – ") ||
			"Service period",
		periodStatus: mapRevenueStatusFromApi(row.periodStatus),
		uploadedAt: asString(row.uploadedAt).trim() || null,
		uploadedByName: parseNamedPerson(row.uploadedBy),
		hasUploadedData: asBoolean(row.hasUploadedData, false),
		revenueLocked: asBoolean(row.revenueLocked, false),
		canUpload: asBoolean(row.canUpload, true),
		canEditPayout: asBoolean(row.canEditPayout, true),
		canEditRevenue: asBoolean(row.canEditRevenue, true),
	};
}

function parseUnitListItem(raw: unknown): RevenueUnitListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id ?? row.unitId).trim();
	if (!id) return null;
	const owners = Array.isArray(row.owners) ? row.owners : [];
	const primaryOwner =
		owners
			.map((owner) => asRecord(owner))
			.find((owner) => owner && asBoolean(owner.isPrimary, false)) ??
		asRecord(owners[0]);
	const ownerName =
		asString(row.ownerName).trim() ||
		parseNamedPerson(row.owner) ||
		asString(primaryOwner?.fullName).trim() ||
		"—";
	const ownerEmail =
		asString(row.ownerEmail).trim() || asString(primaryOwner?.email).trim();
	const ownerList = owners
		.map((owner) => asRecord(owner))
		.filter((owner): owner is Record<string, unknown> => Boolean(owner))
		.map((owner) => ({
			name: asString(owner.fullName ?? owner.name).trim() || "—",
			email: asString(owner.email).trim(),
		}))
		.filter((owner) => owner.name !== "—" || owner.email);
	const status = mapRevenueUnitStatusFromApi(
		row.status ?? row.statusLabel ?? row.payoutStatus,
	);
	const balanceRaw = row.balanceToPay ?? row.balance;
	const isPaid =
		asBoolean(row.isPaid, false) ||
		status === UnitPayoutStatus.SETTLED ||
		balanceRaw === null;
	const payoutLabel =
		asString(row.payoutMethodLabel).trim() ||
		mapRevenuePayoutMethodLabel(row.payoutMethod);
	return {
		id,
		unitCode:
			asString(row.unit ?? row.unitCode ?? row.unitNumber).trim() || "—",
		ownerName,
		ownerEmail,
		owners:
			ownerList.length > 0
				? ownerList
				: [{ name: ownerName, email: ownerEmail }],
		nights: asNumber(row.nights ?? row.reservationNights ?? row.rentalNights),
		netRevenue: asNumber(row.netRevenue ?? row.netRoomRevenue),
		ownerShare: asNumber(row.ownerShare),
		balanceToPay: isPaid ? null : asNumber(balanceRaw),
		payoutMethod: payoutLabel,
		status,
		isPaid,
	};
}

export function parseRevenueServicePeriodUnitsResponse(
	raw: unknown,
	fallback: { propertyId: string; periodId: string },
): RevenueServicePeriodUnitsResponse {
	const data = unwrapData(raw);
	const items = Array.isArray(data.items)
		? data.items
				.map(parseUnitListItem)
				.filter((item): item is RevenueUnitListItem => Boolean(item))
		: [];
	const stats = asRecord(data.stats) ?? {};
	return {
		header: parseRevenuePeriodUnitsHeader(data.header, fallback),
		stats: {
			totalUnits: asNumber(stats.totalUnits, items.length),
			paidOut: asNumber(stats.paidOut),
			outstanding: asNumber(stats.outstanding),
			totalPayout: asNumber(stats.totalPayout),
		},
		items,
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
	};
}

export function buildRevenueServicePeriodUnitsParams(
	args: GetRevenueServicePeriodUnitsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
		status: args.status?.trim() || "ALL",
		payoutMethod: args.payoutMethod?.trim() || "ALL",
		sortBy: args.sortBy?.trim() || "UNIT",
		sortOrder: toApiSortOrder(args.sortOrder),
	};
	if (args.search?.trim()) params.search = args.search.trim();
	return params;
}

function parseOwners(raw: unknown): RevenueUnitOwner[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			const row = asRecord(item);
			if (!row) return null;
			const fullName = asString(row.fullName ?? row.name).trim();
			return {
				customerId: asString(row.customerId ?? row.id),
				fullName,
				email: asString(row.email),
				phone: asString(row.phone),
				initials:
					asString(row.initials).trim() || initialsFromName(fullName) || "—",
				propertyName: asString(row.propertyName),
				unit: asString(row.unit),
				unitType: asString(row.unitType),
				isPrimary: asBoolean(row.isPrimary, false),
			} satisfies RevenueUnitOwner;
		})
		.filter((item): item is RevenueUnitOwner => Boolean(item));
}

function parseInvoiceDetails(raw: unknown): RevenueOwnerInvoiceDetail[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item, index) => {
			const row = asRecord(item);
			if (!row) return null;
			return {
				id: asString(row.id).trim() || `invoice-${index}`,
				ownerName: asString(row.ownerName),
				unit: asString(row.unit),
				invoiceNumber: asString(row.invoiceNumber),
				startDate: toDateInputValue(asString(row.startDate)),
				endDate: toDateInputValue(asString(row.endDate)),
			} satisfies RevenueOwnerInvoiceDetail;
		})
		.filter((item): item is RevenueOwnerInvoiceDetail => Boolean(item));
}

function parseActivityDetailRows(raw: unknown): RevenueActivityDetailRow[] {
	const source = Array.isArray(raw)
		? raw
		: Array.isArray(asRecord(raw)?.rows)
			? (asRecord(raw)?.rows as unknown[])
			: [];
	return source
		.map((item, index) => {
			const row = asRecord(item);
			if (!row) return null;
			return {
				id: asString(row.id).trim() || `activity-${index}`,
				period: asString(row.period),
				periodLabel: asString(row.periodLabel).trim() || asString(row.period),
				unit: asString(row.unit),
				unitType: asString(row.unitType ?? row.type),
				arrivalDate: toDateInputValue(asString(row.arrivalDate ?? row.arrival)),
				departureDate: toDateInputValue(
					asString(row.departureDate ?? row.departure),
				),
				reservationNights: asNumber(
					row.reservationNights ?? row.resvNights ?? row.nights,
				),
				netRoomRevenue: asNumber(row.netRoomRevenue),
			} satisfies RevenueActivityDetailRow;
		})
		.filter((item): item is RevenueActivityDetailRow => Boolean(item));
}

function parseActivitySummaryRows(
	raw: unknown,
	periods: string[],
): RevenueActivitySummaryRow[] {
	const source = Array.isArray(raw)
		? raw
		: Array.isArray(asRecord(raw)?.rows)
			? (asRecord(raw)?.rows as unknown[])
			: [];
	return source
		.map((item, index) => {
			const row = asRecord(item);
			if (!row) return null;
			const valuesRaw = Array.isArray(row.values) ? row.values : [];
			const values = periods.map((period) => {
				const match = valuesRaw
					.map((entry) => asRecord(entry))
					.find((entry) => asString(entry?.period) === period);
				return {
					period,
					amount: asNumber(match?.amount ?? row[period]),
				};
			});
			return {
				id: asString(row.id).trim() || `summary-${index}`,
				itemName: asString(row.itemName ?? row.lineItem),
				itemKey: asString(row.itemKey),
				isDeduction: asBoolean(row.isDeduction, false),
				isTotal: asBoolean(row.isTotal ?? row.isSubtotal, false),
				isFinalPayout: asBoolean(row.isFinalPayout ?? row.isGrandTotal, false),
				values,
				total: asNumber(row.total),
			} satisfies RevenueActivitySummaryRow;
		})
		.filter((item): item is RevenueActivitySummaryRow => Boolean(item));
}

function parsePayout(raw: unknown): RevenueUnitPayout {
	const row = asRecord(raw) ?? {};
	const method =
		mapRevenuePayoutMethodToApi(row.payoutMethod) || "BANK_TRANSFER";
	return {
		payoutMethod: method,
		payoutMethodLabel:
			asString(row.payoutMethodLabel).trim() ||
			mapRevenuePayoutMethodLabel(method),
		payoutRefId: asString(row.payoutRefId ?? row.invoiceNumber),
		netRevenue: asNumber(row.netRevenue),
		ownerShare: asNumber(row.ownerShare),
		balanceToPay: asNumber(row.balanceToPay),
		note: asString(row.note),
		updatedAt: asString(row.updatedAt),
	};
}

export function parseRevenueUnitStatement(
	raw: unknown,
	fallback: { propertyId: string; periodId: string; unitId: string },
): RevenueUnitDetail {
	const data = unwrapData(raw);
	const header = parseRevenuePeriodUnitsHeader(data.header, fallback);
	const statement = asRecord(data.statement) ?? {};
	const actions = asRecord(data.actions) ?? {};
	const owners = parseOwners(data.owners);
	const primaryOwner =
		owners.find((owner) => owner.isPrimary) ?? owners[0] ?? null;
	const unitCode =
		asString(data.unit ?? data.unitCode).trim() || primaryOwner?.unit || "—";
	const unitType =
		asString(data.unitType).trim() || primaryOwner?.unitType || "";
	const ownerName =
		asString(primaryOwner?.fullName).trim() ||
		asString(data.ownerName).trim() ||
		"—";
	const activitySummary = asRecord(data.activitySummary) ?? {};
	let periods = Array.isArray(activitySummary.periods)
		? activitySummary.periods.map((period) => asString(period)).filter(Boolean)
		: [];
	if (periods.length === 0 && Array.isArray(activitySummary.rows)) {
		const firstRow = asRecord(activitySummary.rows[0]);
		const firstValues = Array.isArray(firstRow?.values) ? firstRow.values : [];
		periods = firstValues
			.map((entry) => asString(asRecord(entry)?.period))
			.filter(Boolean);
	}
	const periodLabels = Array.isArray(activitySummary.periodLabels)
		? activitySummary.periodLabels.map((label) => asString(label))
		: periods;
	const summaryRows = parseActivitySummaryRows(activitySummary, periods);
	const finalPayout = [...summaryRows]
		.reverse()
		.find((row) => row.isFinalPayout);
	const statementDateIso = toDateInputValue(asString(statement.statementDate));
	const payoutDateIso = toDateInputValue(asString(statement.payoutDate));
	const statusApi =
		asString(statement.status)
			.trim()
			.toUpperCase()
			.replace(/[\s-]+/g, "_") || "SETTLED";
	const payout = parsePayout(data.payout);

	return {
		id: asString(data.id, fallback.unitId),
		propertyId: header.propertyId,
		periodId: header.periodId,
		breadcrumbUnitLabel: `${unitCode} - ${ownerName}`,
		currency: asString(data.currency, "AED"),
		unitCode,
		unitType,
		ownerName,
		ownerInitials:
			asString(primaryOwner?.initials).trim() ||
			initialsFromName(ownerName) ||
			"—",
		ownerPhone: asString(primaryOwner?.phone),
		ownerEmail: asString(primaryOwner?.email),
		unitDetail: [header.propertyName, unitCode, unitType]
			.filter(Boolean)
			.join(" · "),
		status: mapRevenueUnitStatusFromApi(
			statement.status ?? statement.statusLabel,
		),
		statusApi,
		servicePeriodLabel:
			asString(statement.periodLabel).trim() || header.periodLabel,
		statementId: asString(statement.statementId).trim() || "—",
		statementDate:
			formatShortDate(asString(statement.statementDate)) || statementDateIso,
		payoutDate:
			formatShortDate(asString(statement.payoutDate)) || payoutDateIso,
		statementDateIso,
		payoutDateIso,
		owners,
		ownerInvoiceDetails: parseInvoiceDetails(data.ownerInvoiceDetails),
		activityDetailRows: parseActivityDetailRows(data.activityDetail),
		activitySummaryPeriods: periods,
		activitySummaryPeriodLabels:
			periodLabels.length === periods.length ? periodLabels : periods,
		activitySummaryRows: summaryRows,
		payout,
		actions: {
			canEdit: asBoolean(actions.canEdit, true),
			canEditPayout: asBoolean(actions.canEditPayout, header.canEditPayout),
			canEditRevenue: asBoolean(actions.canEditRevenue, header.canEditRevenue),
			canDownloadCsv: asBoolean(actions.canDownloadCsv, true),
			canDownloadStatement: asBoolean(actions.canDownloadStatement, true),
		},
		expectedUpdatedAt: payout.updatedAt || null,
		header,
		totalAmountToTransfer: asNumber(finalPayout?.total ?? payout.balanceToPay),
	};
}

export function cloneRevenueUnitDetail(
	detail: RevenueUnitDetail,
): RevenueUnitDetail {
	return structuredClone(detail);
}

export function buildUpdateRevenueUnitStatementBody(
	detail: RevenueUnitDetail,
): UpdateRevenueUnitStatementBody {
	return {
		...(detail.expectedUpdatedAt
			? { expectedUpdatedAt: detail.expectedUpdatedAt }
			: {}),
		statement: {
			statementDate: detail.statementDateIso,
			payoutDate: detail.payoutDateIso,
			status: detail.statusApi || "SETTLED",
		},
		payout: {
			payoutMethod:
				mapRevenuePayoutMethodToApi(detail.payout.payoutMethod) ||
				"BANK_TRANSFER",
			payoutRefId: detail.payout.payoutRefId,
			netRevenue: detail.payout.netRevenue,
			ownerShare: detail.payout.ownerShare,
			balanceToPay: detail.payout.balanceToPay,
			note: detail.payout.note,
		},
		unitType: detail.unitType,
		ownerInvoiceDetails: detail.ownerInvoiceDetails.map((row) => ({
			ownerName: row.ownerName,
			unit: row.unit,
			invoiceNumber: row.invoiceNumber,
			startDate: row.startDate,
			endDate: row.endDate,
		})),
		activityDetail: detail.activityDetailRows.map((row) => ({
			period: row.period,
			unit: row.unit,
			unitType: row.unitType,
			arrivalDate: row.arrivalDate,
			departureDate: row.departureDate,
			reservationNights: row.reservationNights,
			netRoomRevenue: row.netRoomRevenue,
		})),
		activitySummary: detail.activitySummaryRows.map((row) => ({
			itemName: row.itemName,
			itemKey: row.itemKey,
			isDeduction: row.isDeduction,
			isTotal: row.isTotal,
			values: row.values.map((value) => ({
				period: value.period,
				amount: value.amount,
			})),
		})),
	};
}

export function formatRevenueDisplayDate(value: string): string {
	return formatShortDate(value) || "—";
}
